// Header field resolver registry for Report Builder templates — turns a
// template's headerFields into displayable label/value pairs.
// See docs/Report-Builder-Implementation-Plan.md.
//
// The one rule: empty fields disappear. A resolver returning null (no data,
// blank manual value) omits the row entirely — same pattern the old header
// used for its conditional Setting row.

import { Post } from '../models/post';
import { ChildProfile } from '../models/childProfile';
import { HeaderField, ReportTemplateConfig } from '../models/reportTemplate';
import { getWeekdaysFromPosts } from './postService';
import { formatDateFullWithOrdinal } from './sharedService';
import { AttendanceStats } from './attendanceStatsService';

export interface ReportFieldContext {
  posts: Post[];
  /** Display name — callers historically pass "First Last" as a string. */
  childName?: string | null;
  /** Full profile when available; enables alias/initials/reference fields. */
  child?: ChildProfile | null;
  /** Already display-formatted (matches ReportView's existing reportDate prop). */
  reportDate?: string | null;
  /** Period bounds, YYYY-MM-DD. Enables the Report Period field. */
  startDate?: string | null;
  endDate?: string | null;
  orgName?: string | null;
  authorName?: string | null;
  templateName?: string | null;
  /** Values entered on the generation panel for manual-editable fields, by field key. */
  fieldValues?: Record<string, string>;
  /** Phase 2 attendance stats for (child, period). Null/absent → attendance
   *  fields resolve empty (row omitted), like any derived field with no data. */
  attendance?: AttendanceStats | null;
}

export interface ResolvedHeaderField {
  key: string;
  label: string;
  value: string;
  fullWidth?: boolean;
}

const blankToNull = (v: string | null | undefined): string | null => {
  const trimmed = v?.trim();
  return trimmed ? trimmed : null;
};

const formatPeriod = (start: string, end: string): string => {
  const fmt = (d: string) => formatDateFullWithOrdinal(new Date(d + 'T00:00'), false);
  return start === end ? fmt(start) : `${fmt(start)} – ${fmt(end)}`;
};

/**
 * Derived resolvers by preset key. Each uses only what it needs from the
 * context and returns null when its data is absent, which omits the row.
 * Phase 2 adds the attendance-derived presets here (and attendance/schedule
 * data to the context).
 */
const derivedResolvers: Record<string, (ctx: ReportFieldContext) => string | null> = {
  // Falls back to "today" like the old hardcoded row did.
  report_date: (ctx) =>
    blankToNull(ctx.reportDate) ?? formatDateFullWithOrdinal(new Date().toISOString(), false),
  report_period: (ctx) =>
    ctx.startDate && ctx.endDate ? formatPeriod(ctx.startDate, ctx.endDate) : null,
  report_type: (ctx) => blankToNull(ctx.templateName),
  report_author: (ctx) => blankToNull(ctx.authorName),
  setting: (ctx) => blankToNull(ctx.orgName),
  child_name: (ctx) =>
    blankToNull(ctx.childName) ??
    blankToNull(ctx.child ? `${ctx.child.first_name} ${ctx.child.last_name}` : null),
  child_alias: (ctx) => blankToNull(ctx.child?.alias),
  child_initials: (ctx) => blankToNull(ctx.child?.initials),
  child_ref: (ctx) => blankToNull(ctx.child?.unique_id),
  // Schedule-derived when attendance stats are available (the truthful
  // source); falls back to weekdays inferred from posts otherwise, which is
  // the pre-template behaviour. Never forces an attendance fetch on its own.
  session_days: (ctx) => {
    const scheduled = ctx.attendance?.sessionDays;
    if (scheduled && scheduled.length > 0) return scheduled.join(', ');
    return blankToNull(getWeekdaysFromPosts(ctx.posts));
  },

  // ── Phase 2 attendance-derived. Omit the row when there's no schedule in
  // the period (hasSchedule false), matching "derived field with no data →
  // row omitted". ──
  sessions_attended: (ctx) =>
    ctx.attendance?.hasSchedule ? `${ctx.attendance.sessionsAttended}` : null,
  sessions_scheduled: (ctx) =>
    ctx.attendance?.hasSchedule ? `${ctx.attendance.sessionsScheduled}` : null,
  attendance_rate: (ctx) =>
    ctx.attendance?.hasSchedule && ctx.attendance.attendanceRate !== null
      ? `${ctx.attendance.attendanceRate}%`
      : null,
  hours_attended: (ctx) =>
    ctx.attendance?.hasSchedule ? `${ctx.attendance.hoursAttended} hrs` : null,
  days_attended: (ctx) =>
    ctx.attendance?.hasSchedule ? `${ctx.attendance.daysAttended}` : null,
  // "2 — 1 illness, 1 family" when reasons are recorded; bare count otherwise.
  absences: (ctx) => {
    const a = ctx.attendance;
    if (!a?.hasSchedule) return null;
    if (a.sessionsNoShow === 0) return '0';
    const breakdown = a.noShowReasons
      .map(r => `${r.count} ${r.reason.toLowerCase()}`)
      .join(', ');
    return breakdown ? `${a.sessionsNoShow} — ${breakdown}` : `${a.sessionsNoShow}`;
  },
  // Neutral phrasing (grace-minutes based): "Arrived on time: 9 of 10 sessions".
  punctuality: (ctx) => {
    const a = ctx.attendance;
    if (!a?.hasSchedule || a.arrivalsTotal === 0) return null;
    return `Arrived on time: ${a.arrivalsOnTime} of ${a.arrivalsTotal} sessions`;
  },
  // Distinct post authors in the period (no attendance fetch needed).
  staff: (ctx) => {
    const names = Array.from(
      new Set(
        ctx.posts
          .map(p => (p.created_by_name ?? '').trim())
          .filter(n => n.length > 0),
      ),
    );
    return names.length > 0 ? names.join(', ') : null;
  },
};

/** Resolve one field to its display value, or null to omit the row. */
export function resolveHeaderField(field: HeaderField, ctx: ReportFieldContext): string | null {
  // A generation-panel entry wins over the template default (manual) or the
  // computed value (derived) — including a deliberately CLEARED input (key
  // present, empty string), which omits the row. Only editable fields accept
  // overrides — a fixed field stays what the template says it is. A field
  // the panel never touched (key absent) falls back to its own source.
  // Required-field enforcement is the generation panel's job.
  if (field.editableAtGeneration && ctx.fieldValues && field.key in ctx.fieldValues) {
    return blankToNull(ctx.fieldValues[field.key]);
  }
  if (field.source === 'manual') {
    return blankToNull(field.defaultValue);
  }
  const resolver = derivedResolvers[field.key];
  return resolver ? resolver(ctx) : null;
}

/** Resolve a template's header fields in order, dropping empty ones. */
export function resolveHeaderFields(
  config: ReportTemplateConfig,
  ctx: ReportFieldContext
): ResolvedHeaderField[] {
  return config.headerFields.flatMap((field) => {
    const value = resolveHeaderField(field, ctx);
    return value === null
      ? []
      : [{ key: field.key, label: field.label, value, fullWidth: field.fullWidth }];
  });
}
